
export interface MotionData {
  x: number; // Acceleration X
  y: number; // Acceleration Y
  z: number; // Acceleration Z
  rotAlpha: number; // Rotation Z
  rotBeta: number;  // Rotation X (Front/Back Tilt)
  rotGamma: number; // Rotation Y (Left/Right Tilt)
  intensity: number; // Calculated overall movement intensity
  jerk: number; // Sudden change in acceleration
}

export enum InstrumentType {
  SYNTH = 'SYNTH',
  PAD = 'PAD',
  PLUCK = 'PLUCK',
  DRONE = 'DRONE',
  DRUMS = 'DRUMS'
}

export enum InteractionMode {
  MOTION = 'MOTION',
  TOUCH = 'TOUCH',
  CAMERA = 'CAMERA'
}

export interface TrackedLandmark {
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
  z?: number;
  visibility?: number;
}

export interface CameraTrackingState {
  isStreaming: boolean;
  isLoadingModel: boolean;
  modelError: string | null;
  hasDetectedBody: boolean;
  landmarks: TrackedLandmark[];
  leftHand: { x: number; y: number; speed: number } | null;
  rightHand: { x: number; y: number; speed: number } | null;
  bodyCenter: { x: number; y: number } | null;
  handSpan: number; // Normalized 0-1
  velocity: number;
  fps: number;
  facingMode: 'user' | 'environment';
  countdown: number | null; // 3, 2, 1, 0, or null
  isCountdownActive: boolean;
}

export interface AudioPreset {
  name: string;
  // Changed from strict OscillatorType to string to allow 'noise'
  oscillatorType: 'sine' | 'square' | 'sawtooth' | 'triangle' | 'noise';
  filterType: BiquadFilterType;
  baseFrequency: number;
  scale: number[]; // MIDI note numbers
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  filterQ: number;
  reverbMix: number;
  delayTime: number;
  pitchMod?: number; // Amount of pitch envelope (octaves) for percussive impact
  description?: string;
  colorHex?: string;
  category?: 'Ambient' | 'Electronic' | 'Pluck' | 'Retro' | 'Space' | 'Acoustic' | 'Mindfulness' | 'Healing' | 'Chakra';
  tuningSystem?: '432Hz' | '528Hz' | '440Hz' | '417Hz' | '639Hz'; // Harmonic sacred tuning
  ambientBed?: 'ocean' | 'singing-bowl' | 'forest-breeze' | 'binaural-theta' | 'warm-drone' | 'none'; // Continuous calming background wash
  harmonicsType?: 'bell' | 'warm-analog' | 'crystal' | 'chime' | 'bamboo' | 'pure';
  detuneSpread?: number; // Gentle organic detune for binaural depth
}

export interface GeminiGenConfigResponse {
  preset: AudioPreset;
  colorHex: string;
  moodDescription: string;
}

export interface PerformanceMetrics {
  durationSeconds: number;
  presetName: string;
  presetCategory?: string;
  averageIntensity: number; // 0 to 1
  peakIntensity: number; // 0 to 1
  tempoStability: number; // 0 to 100 percentage
  smoothnessScore: number; // 0 to 100 percentage
  totalNotesTriggered: number;
  jerkCount: number; // count of abrupt movements
}

export interface BreathingPattern {
  id: string; // 'calm-4-7-8' | 'box-4-4-4-4' | 'coherent-5-5' | 'extended-4-6' | 'restorative-4-4-8'
  name: string; // e.g., '4-7-8 Relaxing Breath', 'Box Breathing (4-4-4-4)'
  badge: string; // e.g., '⚡ High-Intensity Down-Regulation', '🎯 Cadence & Focus Centering'
  reason: string; // Tailored explanation connecting the pattern to this specific session's metrics
  inhale: number; // in seconds
  holdAfterInhale: number; // in seconds
  exhale: number; // in seconds
  holdAfterExhale: number; // in seconds
}

export interface MindfulnessReport {
  sessionId?: string;
  flowStateScore: number; // 0 to 100
  relaxationLevel: string; // e.g., 'Harmonic Zen', 'Deep Alpha Rest', 'Dynamic Flow', 'Grounding Release'
  relaxationScore: number; // 0 to 100
  durationCategory: 'Micro Reset' | 'Flow State' | 'Deep Immersion';
  summary: string;
  mindfulnessHighlights: string[];
  somaticReflection: string;
  recommendedBreathingPace: string;
  breathingPattern?: BreathingPattern;
  wellnessAdvice: string;
  generatedAt: number;
}

export interface Recording {
  id: string;
  url: string;
  blob: Blob;
  timestamp: number;
  name: string;
  mimeType: string;
  metrics?: PerformanceMetrics;
  mindfulnessReport?: MindfulnessReport;
}

export interface SessionDurationOption {
  id: string;
  label: string;
  seconds: number; // 0 = Free Play (Infinite)
  description?: string;
}

export const SESSION_DURATION_PRESETS: SessionDurationOption[] = [
  { id: 'free', label: 'Free Play', seconds: 0, description: 'Continuous open session' },
  { id: '1min', label: '1 Min', seconds: 60, description: 'Quick micro reset (60s)' },
  { id: '3min', label: '3 Min', seconds: 180, description: 'Centering practice (3m)' },
  { id: '5min', label: '5 Min', seconds: 300, description: 'Flow meditation (5m)' },
  { id: '10min', label: '10 Min', seconds: 600, description: 'Deep immersion (10m)' }
];
